import type { PDFPageProxy, PageViewport, RenderTask } from 'pdfjs-dist';

/**
 * The one way the web app loads pdf.js — shared by the portal's pdf-form step (#114) and the HR
 * file viewer (#185), so there is one worker configuration and one import to keep in step.
 *
 * Imported lazily so pdf.js reaches a bundle only when a PDF is actually shown. The worker is
 * resolved through Vite's `?url` so it is served from this origin: BOTH `/portal` and `/app`
 * carry a CSP allowing `worker-src 'self' blob:` and nothing external (staticwebapp.config.json,
 * asserted in portal-shell.test.ts), so a CDN worker would be blocked with no server-side trace.
 * `/app` had no CSP at all until #185 put uploaded PDFs on that page. The bundled compatibility build supports candidate browsers without the
 * newest Map/WeakMap methods; its matching worker comes with it.
 */
/**
 * pdf.js can JIT parts of a font program with `eval`. `script-src 'self'` already refuses that,
 * but a page whose CSP is ever loosened should not silently regain it, so it is off here too.
 */
export const PDF_DOCUMENT_OPTIONS = { isEvalSupported: false } as const;

export async function loadPdfJs() {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  const worker = (await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = worker;
  return pdfjs;
}

/**
 * Draws one page onto a canvas at devicePixelRatio, so it is not soft on a phone, then sized
 * back down by CSS. Returns the render task (cancellable) and the viewport it drew at.
 */
export function renderPdfPage(page: PDFPageProxy, target: HTMLCanvasElement): { readonly task: RenderTask; readonly viewport: PageViewport } | null {
  const ratio = window.devicePixelRatio || 1;
  const viewport = page.getViewport({ scale: ratio });
  target.width = Math.floor(viewport.width);
  target.height = Math.floor(viewport.height);
  target.style.width = `${Math.floor(viewport.width / ratio)}px`;
  target.style.height = `${Math.floor(viewport.height / ratio)}px`;
  const context = target.getContext('2d');
  if (context === null) return null;
  return { task: page.render({ canvas: target, canvasContext: context, viewport }), viewport };
}
